import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api/client';

/**
 * Site-admin repair of a customer's scanners and seats.
 *
 * The customer's own rules are strict on purpose — a released seat waits 30 days before a different
 * scanner may take it, and a scanner seen on too many machines restricts itself — and they have no
 * escape hatch. That is fine until a scanner registers itself with the serial "UNKNOWN" and takes a
 * seat, or one physical unit registers twice under two readings of its serial and holds two. Support
 * could see those and not fix them.
 *
 * Every action here overrides a rule, so the panel leads with WHY a scanner cannot capture rather than
 * with the buttons: the blockers come from the same function the server enforces, so what an admin reads
 * is what is actually true.
 */

interface AdminDevice {
  deviceId: string;
  serialNumber: string;
  name: string | null;
  restricted: boolean;
  retiredUntil: string | null;
  lastUsed: string | null;
  installations: number;
  usable: boolean;
  blockers: string[];
  licenseId: string | null;
  licenseStatus: string | null;
}

interface AdminLicense {
  licenseId: string;
  status: string;
  deviceId: string | null;
  cooldownUntil: string | null;
}

interface Overview {
  devices: AdminDevice[];
  licenses: AdminLicense[];
  /** The active per-device plan, when the account has one. */
  plan?: { includedSeats: number; additionalScanners: number } | null;
}

const shortId = (id: string) => id.slice(0, 8);
const when = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : '—');

export function DevicePanel({ accountId }: { accountId: string }) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  const [moveTarget, setMoveTarget] = useState<Record<string, string>>({});
  const [seatTarget, setSeatTarget] = useState(0);
  const [seatCredit, setSeatCredit] = useState(false);

  const q = useQuery({
    queryKey: ['admin-devices', accountId],
    queryFn: () => api.get<Overview>(`/admin/accounts/${accountId}/devices`),
  });

  /** One mutation for all of it: every action ends in the same reload, and errors read the same way. */
  const act = useMutation({
    mutationFn: async (run: () => Promise<unknown>) => run(),
    onSuccess: () => {
      setError(null);
      setConfirmRemove(null);
      void qc.invalidateQueries({ queryKey: ['admin-devices', accountId] });
      // The customer detail header counts devices and seats, so it is stale the moment this changes.
      void qc.invalidateQueries({ queryKey: ['admin-account', accountId] });
    },
    onError: (e: unknown) => setError(e instanceof ApiError ? e.message : 'That did not work. Try again.'),
  });

  /** The per-device plan and the add-on seats it is billed for, or null when there is no such plan. */
  const plan = q.data?.plan ?? null;
  const billed = plan?.additionalScanners ?? null;
  const devices = q.data?.devices ?? [];
  const licenses = q.data?.licenses ?? [];
  const busy = act.isPending;

  return (
    <div className="panel">
      <div className="section-title" style={{ marginTop: 0 }}>Scanners &amp; seats</div>

      {q.isPending && <p className="cart-note" style={{ textAlign: 'left' }}>// loading…</p>}
      {q.isError && <p className="cart-error">Could not load scanners.</p>}
      {error && <p className="cart-error" style={{ marginTop: 0 }}>{error}</p>}

      {q.data && devices.length === 0 && (
        <p className="cart-note" style={{ textAlign: 'left' }}>// no scanners registered</p>
      )}

      {devices.length > 0 && (
        <table className="tx-table">
          <thead>
            <tr><th>Scanner</th><th>Serial</th><th>State</th><th>Last used</th><th /></tr>
          </thead>
          <tbody>
            {devices.map((d) => (
              <tr key={d.deviceId}>
                <td>
                  {d.name || <span className="dev-unnamed">unnamed</span>}
                  {d.installations > 1 && (
                    <span className="dev-sub"> · {d.installations} installs</span>
                  )}
                </td>
                <td className="mono">{d.serialNumber}</td>
                <td>
                  {d.usable ? (
                    <span className="dev-ok">Capturing</span>
                  ) : (
                    <ul className="dev-blockers">
                      {d.blockers.map((b) => <li key={b}>{b}</li>)}
                    </ul>
                  )}
                  {d.licenseId && <div className="dev-sub">seat {shortId(d.licenseId)}</div>}
                </td>
                <td>{when(d.lastUsed)}</td>
                <td className="dev-actions">
                  {d.restricted && (
                    <button
                      className="btn btn-ghost btn-sm"
                      disabled={busy}
                      onClick={() => act.mutate(() => api.patch(`/admin/accounts/${accountId}/devices/${d.deviceId}`, { restricted: false }))}
                    >
                      Clear restriction
                    </button>
                  )}
                  {confirmRemove === d.deviceId ? (
                    <>
                      <button
                        className="btn btn-primary btn-sm"
                        disabled={busy}
                        onClick={() => act.mutate(() => api.del(`/admin/accounts/${accountId}/devices/${d.deviceId}`))}
                      >
                        {d.licenseId ? 'Remove and free the seat' : 'Really remove'}
                      </button>
                      <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => setConfirmRemove(null)}>Cancel</button>
                    </>
                  ) : (
                    <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => { setConfirmRemove(d.deviceId); setError(null); }}>
                      Remove
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {licenses.length > 0 && (
        <>
          <div className="section-title">Seats</div>
          <table className="tx-table">
            <thead>
              <tr><th>Seat</th><th>Status</th><th>On</th><th>Move to</th><th /></tr>
            </thead>
            <tbody>
              {licenses.map((l) => {
                const on = devices.find((d) => d.deviceId === l.deviceId);
                const choice = moveTarget[l.licenseId] ?? '';
                return (
                  <tr key={l.licenseId}>
                    <td className="mono">{shortId(l.licenseId)}</td>
                    <td>
                      {l.status}
                      {l.cooldownUntil && (
                        /* The cooldown is what blocks the CUSTOMER; an admin move ignores it, and saying so
                           here stops it being read as a reason the move will fail. */
                        <div className="dev-sub">customer must wait until {when(l.cooldownUntil)}</div>
                      )}
                    </td>
                    <td>{on ? (on.name || on.serialNumber) : <span className="dev-sub">unassigned</span>}</td>
                    <td>
                      <select
                        className="dev-select"
                        value={choice}
                        onChange={(e) => setMoveTarget((m) => ({ ...m, [l.licenseId]: e.target.value }))}
                      >
                        <option value="">Choose a scanner…</option>
                        {devices
                          .filter((d) => d.deviceId !== l.deviceId && !d.restricted)
                          .map((d) => (
                            <option key={d.deviceId} value={d.deviceId}>
                              {d.name || d.serialNumber}
                            </option>
                          ))}
                      </select>
                    </td>
                    <td className="dev-actions">
                      <button
                        className="btn btn-ghost btn-sm"
                        disabled={busy || !choice}
                        onClick={() => act.mutate(() => api.post(`/admin/accounts/${accountId}/licenses/${l.licenseId}`, { action: 'move', toDeviceId: choice }))}
                      >
                        Move
                      </button>
                      {l.deviceId && (
                        <button
                          className="btn btn-ghost btn-sm"
                          disabled={busy}
                          onClick={() => act.mutate(() => api.post(`/admin/accounts/${accountId}/licenses/${l.licenseId}`, { action: 'release' }))}
                        >
                          Release
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}

      {/*
        * Additional scanner seats, and removing one.
        *
        * The customer's own flow defers a reduction to the end of the billing period and gives no credit,
        * so nobody can add a seat, use it and remove it for a refund. Support needs neither restriction: a
        * customer billed for a seat they never asked for should not wait out a month — eleven, on the
        * annual plan. Crediting is opt-in, because a refund is a decision rather than a side effect.
        */}
      {billed !== null && billed > 0 && (
        <>
          <div className="section-title">Additional scanner seats</div>
          <div className="seat-remove">
            <span>
              Billed for <strong>{billed}</strong> additional scanner{billed === 1 ? '' : 's'} on top of the
              plan's included seat{(plan?.includedSeats ?? 1) === 1 ? '' : 's'}.
            </span>
            <label>
              <span>Reduce to</span>
              <select className="dev-select" value={seatTarget} onChange={(e) => setSeatTarget(Number(e.target.value))}>
                {Array.from({ length: billed }, (_, i) => i).map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </label>
            <label className="checkbox">
              <input type="checkbox" checked={seatCredit} onChange={(e) => setSeatCredit(e.target.checked)} />
              <span>Credit the unused part of this period</span>
            </label>
            <button
              className="btn btn-ghost btn-sm"
              disabled={busy}
              onClick={() => act.mutate(() => api.post(`/admin/accounts/${accountId}/scanners`, {
                additional: seatTarget, credit: seatCredit ? 'prorate' : 'none',
              }))}
            >
              Remove {billed - seatTarget} seat{billed - seatTarget === 1 ? '' : 's'}
            </button>
          </div>
          <div className="field-help">
            Takes effect immediately. A seat currently assigned to a scanner is refused — release it above
            first. Adding seats is the customer's own purchase, so the proration is charged.
          </div>
        </>
      )}

      <div className="field-help" style={{ marginTop: 10 }}>
        These actions override the customer-facing rules: a released seat becomes available immediately
        instead of waiting out its 30-day cooldown, and a move does not retire the scanner losing the seat.
        Use <b>Replace</b> on the customer&rsquo;s own Devices page for a unit that genuinely broke.
      </div>
    </div>
  );
}
